import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Steps, Button, Space, Tag, Tooltip, message, Spin, Modal, Pagination, Table } from 'antd';
import { CopyOutlined, ExclamationCircleOutlined, CloseCircleFilled, InfoCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import request from '../../../utils/request';
import PickStepRenderer from './renderers/PickStepRenderer';
import SubmitWrapUpRenderer from './renderers/SubmitWrapUpRenderer';
import DropshipPackRenderer from './renderers/DropshipPackRenderer';
import SortAndCountStepRenderer from './renderers/SortAndCountStepRenderer';

type StageGroup = { stage: number; steps: any[] };

const BatchExecutePage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const batchId = Number(id);
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState<any>(null);
  const [task, setTask] = useState<any>(null);
  const [stages, setStages] = useState<StageGroup[]>([]);
  const [currentStageIdx, setCurrentStageIdx] = useState(0);
  const [stageUiSteps, setStageUiSteps] = useState<any[]>([]);
  const [metricsDraft, setMetricsDraft] = useState<Record<string, any>>({});
  const [tipsCollapsed, setTipsCollapsed] = useState(false);
  const [snapshotVisible, setSnapshotVisible] = useState<boolean>(false);
  const [snapshotLoading, setSnapshotLoading] = useState<boolean>(false);
  const [snapshotData, setSnapshotData] = useState<any>(null);
  const [dropshipSnapshotVisible, setDropshipSnapshotVisible] = useState<boolean>(false);
  const [dropshipSnapshotLoading, setDropshipSnapshotLoading] = useState<boolean>(false);
  const [dropshipSnapshotData, setDropshipSnapshotData] = useState<any>(null);
  const [wuSnapshotVisible, setWuSnapshotVisible] = useState<boolean>(false);
  const [wuSnapshotLoading, setWuSnapshotLoading] = useState<boolean>(false);
  const [wuSnapshotData, setWuSnapshotData] = useState<any>(null);
  const isReadonly = useMemo(() => {
    const bs = String(batch?.status || '').toLowerCase();
    const ts = String(task?.status || '').toLowerCase();
    return bs === 'executed' || bs === 'cancelled' || ts === 'completed' || ts === 'cancelled';
  }, [batch?.status, task?.status]);
  const [snapshotSummary, setSnapshotSummary] = useState<{
    targetSummary: { rows: Array<any>; totals: { need: number; got: number; diff: number; doneCount: number; total: number } };
    containerSummary: { byCont: Record<string, { units: Record<string, number> }>; offShelves: Set<string>; stats: { total_qty: number; kinds: number; containers: number } };
    flags: { hasOver: boolean; hasUnder: boolean; allZero: boolean };
  } | null>(null);

  const loadBatch = useCallback(async () => {
    if (!Number.isFinite(batchId)) return;
    try {
      const resp = await request.get(`/api/v1/instruction/batches/${batchId}/`);
      setBatch(resp?.data || null);
    } catch {
      setBatch(null);
    }
  }, [batchId]);

  const buildStages = useCallback((steps: any[]) => {
    const map = new Map<number, any[]>();
    (steps || []).forEach((s:any) => {
      const stage = Number.isFinite(Number(s?.stage)) ? Number(s.stage) : 0;
      if (!map.has(stage)) map.set(stage, []);
      map.get(stage)!.push(s);
    });
    const list: StageGroup[] = Array.from(map.entries())
      .map(([stage, steps]) => ({
        stage,
        steps: (steps || []).sort((a:any, b:any) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0)),
      }))
      .sort((a, b) => a.stage - b.stage);
    setStages(list);
    if (currentStageIdx >= list.length) setCurrentStageIdx(0);
  }, [currentStageIdx]);

  const loadTask = useCallback(async () => {
    if (!Number.isFinite(batchId)) return;
    setLoading(true);
    try {
      const currentTaskId = Number(task?.id);
      const getLatestTaskByBatch = async () => {
        const resp = await request.get('/api/v1/instruction/batch-exec/tasks/', {
          params: { batch: batchId, page: 1, page_size: 1 } as any,
        });
        const payload = resp?.data;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.results) ? payload.results : []);
        return list[0] || null;
      };
      let t: any = null;
      if (Number.isFinite(currentTaskId) && currentTaskId > 0) {
        const resp = await request.get(`/api/v1/instruction/batch-exec/tasks/${currentTaskId}/`);
        t = resp?.data || null;
      } else {
        const bResp = await request.get(`/api/v1/instruction/batches/${batchId}/`);
        const b = bResp?.data || null;
        setBatch(b);
        const bs = String(b?.status || '').toLowerCase();
        if (bs === 'executed' || bs === 'cancelled') {
          t = await getLatestTaskByBatch();
          if (!t) message.info('该批次已结束，当前无可回看的执行任务');
        } else {
          // 单批次单任务：首次进入才 claim，已结束批次不再 claim
          const resp = await request.post(`/api/v1/instruction/batches/${batchId}/exec/claim/`, {});
          t = resp?.data || null;
        }
      }
      setTask(t);
      buildStages(Array.isArray(t?.steps) ? t.steps : []);
    } catch (e:any) {
      const detail = String(e?.response?.data?.detail || '');
      if (detail === 'batch_execution_closed') {
        try {
          const resp = await request.get('/api/v1/instruction/batch-exec/tasks/', {
            params: { batch: batchId, page: 1, page_size: 1 } as any,
          });
          const payload = resp?.data;
          const list = Array.isArray(payload)
            ? payload
            : (Array.isArray(payload?.results) ? payload.results : []);
          const latest = list[0] || null;
          if (latest) {
            setTask(latest);
            buildStages(Array.isArray(latest?.steps) ? latest.steps : []);
            message.info('该批次已执行完成，当前为只读回看模式');
          } else {
            setTask(null);
            setStages([]);
            message.error('该批次已执行完成且无可回看任务');
          }
        } catch {
          message.error('加载已完成批次的执行任务失败');
          setTask(null);
          setStages([]);
        }
      } else {
        message.error(e?.response?.data?.message || e?.response?.data?.detail || '加载批次执行失败');
        setTask(null);
        setStages([]);
      }
    } finally {
      setLoading(false);
    }
  }, [batchId, buildStages, task?.id]);

  const loadStageUi = useCallback(async () => {
    if (!task?.id || stages.length === 0) return;
    const stageNo = stages[currentStageIdx]?.stage;
    if (!Number.isFinite(Number(stageNo))) return;
    try {
      const resp = await request.get(`/api/v1/instruction/batch-exec/tasks/${task.id}/stage-ui`, {
        params: { stage: stageNo, t: Date.now() } as any
      });
      const steps = Array.isArray(resp?.data?.steps) ? resp.data.steps : [];
      setStageUiSteps(steps);
    } catch {
      setStageUiSteps([]);
    }
  }, [task, stages, currentStageIdx]);

  useEffect(() => {
    loadBatch();
    loadTask();
  }, [loadBatch, loadTask]);

  useEffect(() => {
    loadStageUi();
  }, [loadStageUi]);

  const currentStage = stages[currentStageIdx];
  const currentSteps = useMemo(() => {
    if (!currentStage) return [];
    if (stageUiSteps.length === 0) return currentStage.steps || [];
    const byId = new Map(stageUiSteps.map((s:any) => [String(s?.id), s]));
    return (currentStage.steps || []).map((s:any) => byId.get(String(s?.id)) || s);
  }, [currentStage, stageUiSteps]);
  const wrapUpSubmitGate = useMemo(() => {
    const stepsArr = Array.isArray(currentSteps) ? currentSteps : [];
    const wrapStep = stepsArr.find((s: any) => {
      const t = String(s?.step_type || s?.renderer_key || '').toUpperCase();
      return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
    });
    if (!wrapStep) {
      return { hasWrapUp: false, blocked: false, total: 0, done: 0, todo: 0 };
    }
    const sid = String(Number(wrapStep?.id || 0) || '');
    const md = (sid && metricsDraft?.[sid]) ? (metricsDraft[sid] || {}) : (wrapStep?.collected_metrics || {});
    const gate = (md && typeof md === 'object') ? (md.put_away_gate || {}) : {};
    const total = Number(gate?.total || 0);
    const done = Number(gate?.done || 0);
    const todo = Number(gate?.todo || 0);
    const blocked = total > 0 && (todo > 0 || done < total);
    return { hasWrapUp: true, blocked, total, done, todo };
  }, [currentSteps, metricsDraft]);

  const setMetricByStep = useCallback((sid: string, key: string, val: any) => {
    const sidStr = String(sid || '');
    if (!sidStr) return;
    setMetricsDraft(prev => ({ ...prev, [sidStr]: { ...(prev?.[sidStr] || {}), [key]: val } }));
  }, []);

  const computePickStats = (pickLogs: any[]) => {
    const total_qty = (pickLogs || []).reduce((s, r) => s + (Number(r?.qty || 0) || 0), 0);
    const kinds = new Set((pickLogs || []).map((r:any)=> String(r?.target_code || '').trim()).filter(Boolean)).size;
    const containers = new Set((pickLogs || []).map((r:any)=> String(r?.container_code || '').trim()).filter(Boolean)).size;
    return { total_qty, kinds, containers };
  };

  const buildTargetSummary = (pickRows: any[], pickLogs: any[]) => {
    const targetNeed: Record<string, { target: string; need: number; mark?: string }> = {};
    const actualPick: Record<string, number> = {};
    (Array.isArray(pickRows) ? pickRows : []).forEach((row:any) => {
      const t = String(row?.target_label || '').trim();
      if (!t) return;
      const need = Number(row?.target_qty || 0);
      const mark = (row?.product_sorting_code || row?.sorting_code || '') as string;
      if (!targetNeed[t]) targetNeed[t] = { target: t, need: 0, mark };
      targetNeed[t].need += (Number.isFinite(need) ? need : 0);
      if (mark && !targetNeed[t].mark) targetNeed[t].mark = mark;
    });
    (Array.isArray(pickLogs) ? pickLogs : []).forEach((r:any) => {
      const t = String(r?.target_code || '').trim();
      const q = Number(r?.qty || 0);
      if (!t || !(q > 0)) return;
      actualPick[t] = (actualPick[t] || 0) + q;
    });
    const rows = Object.keys(targetNeed).map((k) => {
      const need = Number(targetNeed[k].need || 0);
      const got = Number(actualPick[k] || 0);
      const diff = need - got;
      return { target: k, mark: targetNeed[k].mark || '-', need, got, diff, done: diff <= 0 };
    });
    const totals = rows.reduce((acc, r) => {
      acc.need += r.need; acc.got += r.got;
      if (r.done) acc.doneCount += 1;
      return acc;
    }, { need: 0, got: 0, diff: 0, doneCount: 0, total: rows.length } as any);
    totals.diff = totals.need - totals.got;
    return { rows, totals };
  };

  const buildContainerSummary = (pickLogs: any[]) => {
    const byCont: Record<string, { units: Record<string, number> }> = {};
    const offShelves = new Set<string>();
    (Array.isArray(pickLogs) ? pickLogs : []).forEach((r:any) => {
      const c = String(r?.container_code || '').trim();
      const u = String(r?.target_code || '').trim();
      const q = Number(r?.qty || 0);
      if (!c || !u || !(q > 0)) return;
      if (!byCont[c]) byCont[c] = { units: {} };
      byCont[c].units[u] = (byCont[c].units[u] || 0) + q;
      if (r?.off_shelf) offShelves.add(c);
    });
    const totalQty = (Array.isArray(pickLogs) ? pickLogs : []).reduce((s:number, r:any)=> s + (Number(r?.qty||0) || 0), 0);
    const kinds = new Set((Array.isArray(pickLogs) ? pickLogs : []).map((r:any)=> String(r?.target_code || '').trim()).filter(Boolean)).size;
    const containers = new Set((Array.isArray(pickLogs) ? pickLogs : []).map((r:any)=> String(r?.container_code || '').trim()).filter(Boolean)).size;
    return { byCont, offShelves, stats: { total_qty: totalQty, kinds, containers } };
  };

  const computeFlags = (targetSummary: { rows: Array<any> }) => {
    const hasOver = targetSummary.rows.some((r:any) => r.diff < 0);
    const hasUnder = targetSummary.rows.some((r:any) => r.diff > 0);
    const allZero = targetSummary.rows.length > 0 && targetSummary.rows.every((r:any) => r.diff === 0);
    return { hasOver, hasUnder, allZero };
  };

  const renderResultTitle = (titleText: string, flags: { hasOver: boolean; hasUnder: boolean; allZero: boolean }) => {
    const { hasOver, hasUnder } = flags;
    const chipText = hasOver ? '存在超额拣货' : (hasUnder ? '存在拣货不足' : '拣货目标正好完成');
    const chipStyle: React.CSSProperties = hasOver
      ? { marginLeft: 12, fontWeight: 600, color:'#a8071a', background:'#fff1f0', border:'1px solid #ffa39e', padding:'2px 8px', borderRadius: 4 }
      : hasUnder
        ? { marginLeft: 12, fontWeight: 600, color:'#0958d9', background:'#e6f4ff', border:'1px solid #91caff', padding:'2px 8px', borderRadius: 4 }
        : { marginLeft: 12, fontWeight: 600, color:'#135200', background:'#f6ffed', border:'1px solid #b7eb8f', padding:'2px 8px', borderRadius: 4 };
    return (
      <div style={{ display:'flex', alignItems:'center' }}>
        {hasOver ? (
          <CloseCircleFilled style={{ color:'#ff4d4f', marginRight: 8, fontSize: 18 }} />
        ) : hasUnder ? (
          <InfoCircleFilled style={{ color:'#1677ff', marginRight: 8, fontSize: 18 }} />
        ) : (
          <CheckCircleFilled style={{ color:'#52c41a', marginRight: 8, fontSize: 18 }} />
        )}
        <span style={{ fontWeight: 600 }}>{titleText}</span>
        <span style={chipStyle}>{chipText}</span>
      </div>
    );
  };

  const PickSnapshotPagedContent: React.FC<{
    targetSummary: any;
    containerSummary: any;
    headerRecordCount: React.ReactNode;
    detailPickLogs: any[];
  }> = ({ targetSummary, containerSummary, headerRecordCount, detailPickLogs }) => {
    const [targetPage, setTargetPage] = useState<number>(1);
    const [targetPageSize, setTargetPageSize] = useState<number>(50);
    const [contPage, setContPage] = useState<number>(1);
    const [contPageSize, setContPageSize] = useState<number>(10);

    const targetRowsAll: any[] = Array.isArray(targetSummary?.rows) ? targetSummary.rows : [];
    const targetPaged = useMemo(() => {
      const p = Math.max(1, targetPage);
      const ps = Math.max(1, targetPageSize);
      const start = (p - 1) * ps;
      return targetRowsAll.slice(start, start + ps);
    }, [targetRowsAll, targetPage, targetPageSize]);

    const contListAll = useMemo(() => {
      try {
        const keys = Object.keys(containerSummary?.byCont || {});
        return keys.sort((a, b) => {
          const aOff = containerSummary?.offShelves?.has?.(a) ? 1 : 0;
          const bOff = containerSummary?.offShelves?.has?.(b) ? 1 : 0;
          return bOff - aOff || String(a).localeCompare(String(b));
        });
      } catch {
        return [];
      }
    }, [containerSummary]);
    const contListPaged = useMemo(() => {
      const p = Math.max(1, contPage);
      const ps = Math.max(1, contPageSize);
      const start = (p - 1) * ps;
      return contListAll.slice(start, start + ps);
    }, [contListAll, contPage, contPageSize]);

    return (
      <div style={{ lineHeight: 1.6, display:'flex', flexDirection:'column', gap:12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700 }}>拣货结果汇总（目标物）</div>
            <div style={{ color:'#595959' }}>
              目标数 {targetSummary.totals.total} ｜ 全部拣出 {targetSummary.totals.doneCount} ｜ 目标合计 {targetSummary.totals.need} ｜ 实际拣出 {targetSummary.totals.got} ｜ 不足合计 {targetSummary.totals.diff}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left' }}>目标物</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left' }}>分拣标识</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>目标拣货数量</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>实际拣货数量</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>拣货差额</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>已完成</th>
              </tr>
            </thead>
            <tbody>
              {targetRowsAll.length === 0 ? (
                <tr><td colSpan={6} style={{ border:'1px solid #d9d9d9', padding:6, color:'#999' }}>无目标数据</td></tr>
              ) : targetPaged.map((r:any, idx:number) => {
                const isOver = r.diff < 0;
                const diffStyle = r.diff > 0 ? { color:'#cf1322', fontWeight:700 } : undefined;
                const rowBg = r.done ? { background:'#f6ffed' } : undefined;
                const rowText = isOver ? { color:'#cf1322', fontWeight:700 } : undefined;
                return (
                  <tr key={`${r.target}|${(targetPage - 1) * targetPageSize + idx}`} style={{ ...(rowBg||{}), ...(rowText||{}) }}>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>
                      <span>{r.target}</span>
                      {isOver ? <Tag color="processing" style={{ marginLeft: 8 }}>超额</Tag> : null}
                    </td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{r.mark || '-'}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>{r.need}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>{r.got}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', ...(diffStyle||{}) }}>{r.diff}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>
                      <Tag color={r.done ? 'green' : 'default'}>{r.done ? '已完成' : '未完成'}</Tag>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight:700 }}>Sum</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{targetSummary.totals.need}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{targetSummary.totals.got}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{targetSummary.totals.diff}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }} />
              </tr>
            </tfoot>
          </table>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={targetPage}
              pageSize={targetPageSize}
              total={targetRowsAll.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['20','50','100','200'] as any}
              onChange={(p, ps) => { setTargetPage(p); setTargetPageSize(ps); }}
            />
          </div>
        </div>

        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700 }}>涉及拣货记录汇总</div>
            <div style={{ color:'#595959' }}>
              记录 {headerRecordCount} ｜ 容器 {containerSummary.stats.containers} ｜ 数量 {containerSummary.stats.total_qty} ｜ <span style={{ color:'#a8071a', fontWeight: 700 }}>已下架容器 {containerSummary.offShelves.size}</span>
            </div>
          </div>
          {Object.keys(containerSummary.byCont || {}).length === 0 ? (
            <div style={{ color:'#999' }}>无拣货记录</div>
          ) : (
            <>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 220 }}>扫描容器</th>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 260 }}>目标物</th>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 240 }}>目标物标识</th>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', width: 120 }}>拣货数量</th>
                  </tr>
                </thead>
                <tbody>
                  {contListPaged.map((c, ci) => {
                    const rows = (() => {
                      const logs: any[] = Array.isArray(detailPickLogs) ? detailPickLogs : [];
                      const filtered = logs.filter((r:any) => String(r?.container_code || '').trim() === c);
                      if (filtered.length > 0) return filtered.map((r:any) => ({ target_code: String(r?.target_code||''), sorting_mark: String(r?.sorting_mark||'-'), qty: Number(r?.qty||0) }));
                      const units = containerSummary.byCont[c]?.units || {};
                      return Object.entries(units).map(([u, q]: any) => ({ target_code: u, sorting_mark: '-', qty: Number(q || 0) }));
                    })();
                    const groupBg = ((contPage - 1) * contPageSize + ci) % 2 === 0 ? { background:'#fffbe6' } : { background:'#e6f7ff' };
                    return rows.length === 0 ? null : (
                      <React.Fragment key={c}>
                        {rows.map((r:any, idx:number) => (
                          <tr key={`${c}|row|${idx}`} style={groupBg}>
                            {idx === 0 ? (
                              <td rowSpan={rows.length} style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace', verticalAlign:'top' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <span>{c}</span>
                                  {containerSummary.offShelves.has(c) ? (
                                    <Tag style={{ background:'#fff1f0', borderColor:'#ffa39e', color:'#a8071a' }}>已下架</Tag>
                                  ) : null}
                                </div>
                              </td>
                            ) : null}
                            <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>{String(r?.target_code || '')}</td>
                            <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{String(r?.sorting_mark || '-') || '-'}</td>
                            <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>{Number(r?.qty || 0)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight:700 }}>Sum</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{containerSummary.stats.total_qty}</td>
                  </tr>
                </tfoot>
              </table>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                <Pagination
                  size="small"
                  current={contPage}
                  pageSize={contPageSize}
                  total={contListAll.length}
                  showSizeChanger
                  showQuickJumper
                  pageSizeOptions={['5','10','20','50'] as any}
                  onChange={(p, ps) => { setContPage(p); setContPageSize(ps); }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const submitPick = async (stageNo: number) => {
    if (isReadonly) {
      message.warning('该批次已结束，当前为只读回看模式');
      return;
    }
    if (!task?.id) return;
    const pickLogs = Array.isArray((window as any).__PICK_LOGS__) ? (window as any).__PICK_LOGS__ : [];
    const stats = computePickStats(pickLogs);
    await request.post(`/api/v1/instruction/batch-exec/tasks/${task.id}/picking/submissions/`, {
      stage_idx: stageNo,
      idempotency_key: `task_${task.id}_stage_${stageNo}`,
      hash: '',
      ui_context: {},
      pick_logs: pickLogs,
      stats,
    });
  };

  const submitStep = async (stepId: number, metrics: any) => {
    if (isReadonly) {
      message.warning('该批次已结束，当前为只读回看模式');
      return;
    }
    if (!Number.isFinite(stepId)) return;
    await request.post(`/api/v1/instruction/batch-exec/steps/${stepId}/submit/`, { metrics });
  };

  const batchStats = useMemo(() => {
    const instrCount = Array.isArray(batch?.items) ? batch.items.length : 0;
    const s = String(batch?.inventory_unit_summary || '').trim();
    let kind = 0;
    let qty = 0;
    if (s) {
      const [k, q] = s.split('|').map((v) => v.trim());
      kind = Number(k || 0) || 0;
      qty = Number(q || 0) || 0;
    }
    return { instrCount, kind, qty };
  }, [batch]);

  const batchTips = useMemo(() => {
    const list = Array.isArray(batch?.items) ? batch.items : [];
    const map = new Map<string, { count: number; codes: string[] }>();
    list.forEach((r: any) => {
      const text = String(r?.execution_tips || '').trim();
      if (!text) return;
      const code = String(r?.instruction_system_code || r?.system_code || r?.instruction_code || '').trim();
      if (!map.has(text)) map.set(text, { count: 0, codes: [] });
      const it = map.get(text)!;
      it.count += 1;
      if (code) it.codes.push(code);
    });
    const groups = Array.from(map.entries())
      .map(([text, meta]) => ({
        text,
        count: meta.count,
        codes: Array.from(new Set(meta.codes)),
      }))
      .sort((a, b) => b.count - a.count);
    return { groups, total: groups.length };
  }, [batch]);

  const isStepDone = (st: any) => String(st?.status || '').toLowerCase() === 'done';
  const isStageCompleted = (arr: any[]) => (arr || []).every((x: any) => isStepDone(x));
  const getStageDisplayName = (steps: any[], maxNames = 2) => {
    try {
      const arr = Array.isArray(steps) ? steps : [];
      const hasDropshipPack = arr.some((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'DROPSHIP_PACK');
      if (hasDropshipPack) return '批次执行-一件代发 配货|装箱|贴单';
      const names = arr.map((s:any) => String((s?.title || s?.step_type || '')).trim()).filter(Boolean);
      const uniq = Array.from(new Set(names));
      const head = uniq.slice(0, maxNames).join('、');
      const more = uniq.length > maxNames ? ` 等${uniq.length}步` : '';
      return head + more;
    } catch {
      return '';
    }
  };

  const WrapUpSnapshotBlock: React.FC<{ snapshot: any }> = ({ snapshot }) => {
    const snap = snapshot || {};
    const sections = (snap?.sections || snap?.snapshot?.sections || {}) as any;
    const step1 = sections?.step1_unoperable || {};
    const step2 = sections?.step2_overpick || {};
    const step3 = sections?.step3_put_away || {};
    const step4 = sections?.step4_remarks_attachments || {};
    const unoperableOps = Array.isArray(step1?.unoperable_ops) ? step1.unoperable_ops : [];
    const exceptionContainers = Array.isArray(step1?.exception_containers) ? step1.exception_containers : [];
    const overPickOps = Array.isArray(step2?.over_pick_ops) ? step2.over_pick_ops : [];
    const overPickGroups = Array.isArray(step2?.over_pick_groups) ? step2.over_pick_groups : [];
    const putAwayOps = Array.isArray(step3?.put_away_ops) ? step3.put_away_ops : [];
    const iocNormalCodes = Array.isArray(step3?.ioc_normal_codes) ? step3.ioc_normal_codes : [];
    const overPickDetailRows = useMemo(() => {
      const rows: Array<any> = [];
      (Array.isArray(overPickGroups) ? overPickGroups : []).forEach((g: any, gi: number) => {
        const groupCode = String(g?.group_code || '').trim();
        const tempLocNo = Number(g?.temp_loc_no || 0) || null;
        const instructionId = Number(g?.instruction_id || 0) || null;
        const createdCode = String(g?.created_container_code || '').trim().toUpperCase();
        const createdId = Number(g?.created_container_id || 0) || null;
        const items = Array.isArray(g?.items) ? g.items : [];
        if (items.length === 0) {
          rows.push({
            key: `g_${gi}_empty`,
            sorting_group: groupCode || '-',
            temp_loc_no: tempLocNo ?? '-',
            instruction_id: instructionId ?? '-',
            old_code: '-',
            inventory_unit_code: '-',
            qty: 0,
            created_container_code: createdCode || (createdId ? `#${createdId}` : '-'),
          });
          return;
        }
        items.forEach((it: any, ii: number) => {
          rows.push({
            key: `g_${gi}_i_${ii}`,
            sorting_group: groupCode || '-',
            temp_loc_no: tempLocNo ?? '-',
            instruction_id: instructionId ?? '-',
            old_code: String(it?.old_code || '').trim().toUpperCase() || '-',
            inventory_unit_code: String(it?.inventory_unit_code || '').trim().toUpperCase() || '-',
            qty: Number(it?.qty || 0) || 0,
            created_container_code: createdCode || (createdId ? `#${createdId}` : '-'),
          });
        });
      });
      return rows;
    }, [overPickGroups]);
    const createdScCodes = useMemo(
      () => Array.from(new Set(
        overPickDetailRows
          .map((r) => String(r?.created_container_code || '').trim().toUpperCase())
          .filter((s) => s && s !== '-')
      )),
      [overPickDetailRows],
    );
    const putAwayDetailRows = useMemo(() => {
      return (Array.isArray(putAwayOps) ? putAwayOps : []).map((r: any, idx: number) => {
        const containerCode = String(r?.container_code || '').trim().toUpperCase();
        const locationCode = String(r?.location_code || '').trim().toUpperCase();
        const sourceKind = containerCode.startsWith('IOC-') ? 'IOC' : (containerCode.startsWith('SC-') ? 'SC' : '-');
        return {
          key: `pa_${idx}`,
          container_code: containerCode || '-',
          location_code: locationCode || '-',
          source_kind: sourceKind,
        };
      });
    }, [putAwayOps]);
    const remarks = String(step4?.remarks || '');
    const attachments = Array.isArray(step4?.attachments) ? step4.attachments : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #ffd591', background: '#fff7e6' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>步骤1：无法操作产品生成容器</div>
          <div>{`无法操作记录：${unoperableOps.length} 条`}</div>
          <div>{`异常容器：${exceptionContainers.length} 个`}</div>
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #efdbff', background: '#f9f0ff' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>步骤2：多拣货产品分拣</div>
          <div>{`多拣货记录：${overPickOps.length} 条`}</div>
          <div>{`分组：${overPickGroups.length} 组`}</div>
          <div>{`已生成仓储容器：${createdScCodes.length} 个`}</div>
          {overPickDetailRows.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <Table
                size="small"
                rowKey={(r: any) => String(r?.key || '')}
                pagination={{ pageSize: 8, showSizeChanger: true }}
                dataSource={overPickDetailRows}
                columns={[
                  { title: '分拣组', dataIndex: 'sorting_group', key: 'sorting_group', width: 120 },
                  { title: '临时分拣仓位', dataIndex: 'temp_loc_no', key: 'temp_loc_no', width: 110, align: 'center' as any },
                  { title: '指令ID', dataIndex: 'instruction_id', key: 'instruction_id', width: 90, align: 'center' as any },
                  { title: '分拣条码/扫描值', dataIndex: 'old_code', key: 'old_code', width: 220 },
                  { title: '匹配IU', dataIndex: 'inventory_unit_code', key: 'inventory_unit_code', width: 220 },
                  { title: '数量', dataIndex: 'qty', key: 'qty', width: 90, align: 'right' as any },
                  { title: '生成容器编码', dataIndex: 'created_container_code', key: 'created_container_code', width: 180 },
                ]}
                scroll={{ x: 1050 }}
              />
            </div>
          ) : (
            <div style={{ color: '#8c8c8c', marginTop: 6 }}>暂无分拣明细</div>
          )}
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #91caff', background: '#f0f9ff' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>步骤3：产出容器放入仓位</div>
          <div>{`放仓位记录：${putAwayOps.length} 条`}</div>
          <div>{`IOC（正常）编码：${iocNormalCodes.length} 个`}</div>
          {putAwayDetailRows.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <Table
                size="small"
                rowKey={(r: any) => String(r?.key || '')}
                pagination={{ pageSize: 8, showSizeChanger: true }}
                dataSource={putAwayDetailRows}
                columns={[
                  { title: '容器编码', dataIndex: 'container_code', key: 'container_code', width: 240 },
                  { title: '容器来源', dataIndex: 'source_kind', key: 'source_kind', width: 100, align: 'center' as any },
                  { title: '放入仓位', dataIndex: 'location_code', key: 'location_code', width: 220 },
                ]}
                scroll={{ x: 620 }}
              />
            </div>
          ) : (
            <div style={{ color: '#8c8c8c', marginTop: 6 }}>暂无放仓位明细</div>
          )}
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #b7eb8f', background: '#f6ffed' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>步骤4：备注与附件</div>
          <div style={{ marginBottom: 4 }}>
            <span>备注：</span>
            <span style={{ color: '#cf1322', fontWeight: 600, whiteSpace: 'pre-wrap' }}>
              {remarks || '未填写'}
            </span>
          </div>
          <div>{`附件：${attachments.length} 个（批次模式固定不采集）`}</div>
        </div>
      </div>
    );
  };

  const DropshipPackSnapshotBlock: React.FC<{ snapshot: any }> = ({ snapshot }) => {
    const snap = snapshot || {};
    const stats = snap?.stats || {};
    const totalOrders = Number(stats?.total_orders || 0);
    const doneOrders = Number(stats?.done_orders || 0);
    const unoperatedOrders = Number(stats?.unoperated_orders || 0);
    const showAllCompletedMark = totalOrders > 0 && doneOrders === totalOrders;
    const showPartiallyCompletedMark = !showAllCompletedMark && unoperatedOrders > 0;
    const rows = Array.isArray(snap?.snapshot?.rows) ? snap.snapshot.rows : [];
    const operationRecords = Array.isArray(snap?.snapshot?.operation_records) ? snap.snapshot.operation_records : [];
    const scanMatchRecords = Array.isArray(snap?.snapshot?.scan_match_records) ? snap.snapshot.scan_match_records : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #91caff', background: '#f0f9ff' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 12, minHeight: 120 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>操作进度总表快照</div>
              <div>{`目标${Number(stats?.total_orders || 0)}单${Number(stats?.total_qty || 0)}件`}</div>
              <div style={{ color: '#52c41a' }}>{`已完成${Number(stats?.done_orders || 0)}单${Number(stats?.done_qty || 0)}件`}</div>
              <div style={{ color: '#ff4d4f' }}>{`无法操作${Number(stats?.unoperated_orders || 0)}单${Number(stats?.unoperated_qty || 0)}件`}</div>
              <div style={{ color: '#8c8c8c' }}>{`未标注${Number(stats?.unmarked_orders || 0)}单${Number(stats?.unmarked_qty || 0)}件`}</div>
            </div>
            {showAllCompletedMark ? (
              <div style={{ flex: '0 0 260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    borderRadius: 12,
                    background: '#52c41a',
                    color: '#fff',
                    fontSize: 34,
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  <CheckCircleFilled style={{ fontSize: 38 }} />
                  已全部完成
                </span>
              </div>
            ) : showPartiallyCompletedMark ? (
              <div style={{ flex: '0 0 260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    borderRadius: 12,
                    background: '#fa8c16',
                    color: '#fff',
                    fontSize: 34,
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  <ExclamationCircleOutlined style={{ fontSize: 38 }} />
                  部分完成
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #d9d9d9', background: '#fafafa' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>指令明细（快照）</div>
          {rows.length === 0 ? (
            <div style={{ color: '#8c8c8c' }}>无明细</div>
          ) : (
            <Table
              size="small"
              rowKey={(r: any, i?: number) => `${String(r?.instruction_id || r?.instruction_system_code || '')}_${i || 0}`}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              dataSource={rows}
              columns={[
                { title: '指令系统编码', dataIndex: 'instruction_system_code', key: 'instruction_system_code' },
                { title: '结果', dataIndex: 'result_text', key: 'result_text' },
                { title: '件数', dataIndex: 'row_qty', key: 'row_qty', width: 90, align: 'right' as any },
                { title: '无法操作原因', dataIndex: 'error_reason', key: 'error_reason' },
              ]}
            />
          )}
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #ffd591', background: '#fff7e6' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>操作记录（快照）</div>
          {operationRecords.length === 0 ? (
            <div style={{ color: '#8c8c8c' }}>暂无操作记录</div>
          ) : (
            <Table
              size="small"
              rowKey={(_: any, i?: number) => `op_${i || 0}`}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              dataSource={operationRecords}
              columns={[
                { title: '时间', dataIndex: 'time', key: 'time', width: 180 },
                { title: '标注方式', dataIndex: 'mark_mode', key: 'mark_mode', width: 120 },
                {
                  title: '指令条目',
                  key: 'instructions',
                  render: (_: any, r: any) => {
                    const arr = Array.isArray(r?.instructions) ? r.instructions : [];
                    if (arr.length === 0) return '-';
                    return arr.slice(0, 10).map((it: any, idx: number) => {
                      const code = String(it?.system_code || '-');
                      const status = String(it?.status || '-');
                      const reason = String(it?.error_reason || '').trim();
                      return `${idx + 1}. ${code}｜${status}${reason ? `｜${reason}` : ''}`;
                    }).join('\n');
                  },
                },
              ]}
            />
          )}
        </div>
        <div style={{ padding: 10, borderRadius: 8, border: '1px solid #b7eb8f', background: '#f6ffed' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>运单扫描与匹配记录（快照）</div>
          {scanMatchRecords.length === 0 ? (
            <div style={{ color: '#8c8c8c' }}>暂无扫描与匹配记录</div>
          ) : (
            <Table
              size="small"
              rowKey={(_: any, i?: number) => `scan_${i || 0}`}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              dataSource={scanMatchRecords}
              columns={[
                { title: '时间', dataIndex: 'time', key: 'time', width: 180 },
                { title: '扫描值', dataIndex: 'scanValue', key: 'scanValue', width: 220 },
                { title: '匹配指令', dataIndex: 'matchedInstruction', key: 'matchedInstruction', width: 220 },
                { title: '匹配方式', dataIndex: 'matchMethod', key: 'matchMethod' },
              ]}
            />
          )}
        </div>
      </div>
    );
  };

  const finishCurrentStage = async () => {
    if (isReadonly) {
      message.warning('该批次已结束，当前为只读回看模式');
      return;
    }
    const cur = stages[currentStageIdx];
    if (!cur) return;
    const isLastStage = currentStageIdx >= (stages.length - 1);
    try {
      setLoading(true);
      const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur.stage) : (currentStageIdx + 1);
      const stepsArr = Array.isArray(cur?.steps) ? cur.steps : [];
      // 收尾阶段：先生成快照预览并确认，再执行后续提交
      const hasWrapUp = stepsArr.some((s:any) => {
        const t = String(s?.step_type || s?.renderer_key || '').toUpperCase();
        return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
      });
      if (hasWrapUp && wrapUpSubmitGate.blocked) {
        message.warning(`仍有容器未放入仓位（共${wrapUpSubmitGate.total}个，已放入${wrapUpSubmitGate.done}个，未放入${wrapUpSubmitGate.todo}个），无法提交本阶段`);
        return;
      }
      if (hasWrapUp) {
        const wrapStep = stepsArr.find((s:any) => {
          const t = String(s?.step_type || s?.renderer_key || '').toUpperCase();
          return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
        });
        const wrapStepId = Number(wrapStep?.id);
        const sid = Number.isFinite(wrapStepId) ? String(wrapStepId) : '';
        const md = (sid && metricsDraft[sid]) ? (metricsDraft[sid] || {}) : {};
        let localDraft: any = null;
        if (Number.isFinite(wrapStepId) && task?.id) {
          const draftKey = `WRAPUP_DRAFT_${task.id}_${stageNo}_${wrapStepId}_NO_SESSION`;
          try {
            const raw = localStorage.getItem(draftKey);
            if (raw) localDraft = JSON.parse(raw);
          } catch {
            localDraft = null;
          }
        }
        const arr = (v: any) => (Array.isArray(v) ? v : []);
        const putAwayOps = arr(md?.put_away_ops).length > 0 ? arr(md?.put_away_ops) : arr(localDraft?.put_away_ops);
        const unoperableOps = arr(md?.unoperable_ops).length > 0 ? arr(md?.unoperable_ops) : arr(localDraft?.unoperable_ops);
        const overPickOps = arr(md?.over_pick_ops).length > 0 ? arr(md?.over_pick_ops) : arr(localDraft?.over_pick_ops);
        const overPickGroups = arr(md?.over_pick_groups).length > 0 ? arr(md?.over_pick_groups) : arr(localDraft?.over_pick_groups);
        const exceptionContainers = arr(md?.exception_containers).length > 0 ? arr(md?.exception_containers) : arr(localDraft?.exception_containers);
        const remarks = String((md?.remarks != null ? md.remarks : (localDraft?.remarks != null ? localDraft.remarks : '')) || '');
        const iocNormalCodes = Array.from(new Set(
          putAwayOps
            .map((x:any) => String(x?.container_code || '').trim().toUpperCase())
            .filter(Boolean)
        ));
        const snap = {
          schema_version: 1,
          kind: 'wrap_up',
          sections: {
                  step1_unoperable: { unoperable_ops: unoperableOps, exception_containers: exceptionContainers },
            step2_overpick: { over_pick_ops: overPickOps, over_pick_groups: overPickGroups },
            step3_put_away: { put_away_ops: putAwayOps, ioc_normal_codes: iocNormalCodes },
            step4_remarks_attachments: { remarks, attachments: [] },
          },
        };
        const stats = {
          put_away_ops_count: putAwayOps.length,
          exception_containers_count: exceptionContainers.length,
          over_pick_groups_count: overPickGroups.length,
          attachments_count: 0,
        };
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `确认提交收尾记录并完成本阶段 - 批次：${String(batch?.system_code || `#${batchId}`)}`,
            width: 980,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color: '#595959', marginBottom: 8 }}>
                  收尾阶段快照将用于后续回看与审计（批次模式不采集附件快照）。
                </div>
                <WrapUpSnapshotBlock snapshot={snap} />
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
        if (task?.id) {
          await request.post(`/api/v1/instruction/batch-exec/tasks/${task.id}/wrap-up/submissions/`, {
            stage_idx: stageNo,
            idempotency_key: `batch_task_${task.id}_stage_${stageNo}_wrap_up`,
            hash: '',
            stats,
            snapshot: snap,
          });
        }
      }
      // 中间阶段（配货/装箱/贴单）：先生成快照预览并确认，再执行后续提交
      const hasDropshipPack = stepsArr.some((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'DROPSHIP_PACK');
      if (hasDropshipPack) {
        const curBatchId = Number(batch?.id || batchId || 0);
        const rowsRaw = Array.isArray(batch?.items) ? batch.items : [];
        const loadLocalArray = (key: string) => {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        };
        const loadLocalObject = (key: string) => {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
          } catch {
            return {};
          }
        };
        const marksObj: any = curBatchId > 0 ? loadLocalObject(`dropship-pack-marks-${curBatchId}`) : {};
        const doneSet = new Set<string>((Array.isArray(marksObj?.markedDoneIds) ? marksObj.markedDoneIds : []).map((x: any) => String(x)));
        const unoperatedSet = new Set<string>((Array.isArray(marksObj?.markedUnoperatedIds) ? marksObj.markedUnoperatedIds : []).map((x: any) => String(x)));
        const unmarkedSet = new Set<string>((Array.isArray(marksObj?.markedUnmarkedIds) ? marksObj.markedUnmarkedIds : []).map((x: any) => String(x)));
        const unoperatedReasons: Record<string, string> = (marksObj?.unoperatedReasons && typeof marksObj.unoperatedReasons === 'object')
          ? marksObj.unoperatedReasons
          : {};
        const getRowMarkKeys = (r: any): string[] => {
          const cands = [
            r?.id, // 批次项ID（阶段2页面本地标注通常用这个）
            r?.instruction, // 指令ID
            r?.instruction_id, // 指令ID（兼容字段）
          ];
          return Array.from(
            new Set(
              cands
                .map((x) => String(x ?? '').trim())
                .filter(Boolean),
            ),
          );
        };
        const updates = rowsRaw.map((r: any) => {
          const instrId = Number(r?.instruction ?? r?.instruction_id ?? 0) || 0;
          const keys = getRowMarkKeys(r);
          let status = String(r?.status || '').toLowerCase();
          if (keys.some((k) => doneSet.has(k))) status = 'done';
          else if (keys.some((k) => unoperatedSet.has(k))) status = 'excluded';
          else if (keys.some((k) => unmarkedSet.has(k))) status = 'pending';
          if (!['done', 'excluded', 'pending'].includes(status)) status = 'pending';
          let reason = '';
          if (status === 'excluded') {
            for (const k of keys) {
              const v = String(unoperatedReasons[k] || '').trim();
              if (v) { reason = v; break; }
            }
          }
          const error_reason = reason || String(r?.error_reason || '').trim();
          return {
            instruction_id: instrId,
            status,
            error_reason,
          };
        }).filter((u: any) => Number(u?.instruction_id || 0) > 0);
        const operationRecords = curBatchId > 0
          ? loadLocalArray(`dropship-pack-records-${curBatchId}`).slice(0, 50)
          : [];
        const scanMatchRecords = curBatchId > 0
          ? loadLocalArray(`dropship-pack-scan-records-${curBatchId}`).slice(0, 500)
          : [];
        const toQty = (r: any) => {
          const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
          let q = 0;
          targets.forEach((t: any) => {
            const n = Number(t?.expected_qty || 0);
            if (Number.isFinite(n)) q += n;
          });
          return q;
        };
        const updateByInstr = new Map<number, any>();
        updates.forEach((u: any) => updateByInstr.set(Number(u?.instruction_id || 0), u));
        const rows = rowsRaw.map((r: any) => {
          const instrId = Number(r?.instruction ?? r?.instruction_id ?? 0) || 0;
          const u = updateByInstr.get(instrId);
          const status = String(u?.status || r?.status || '').toLowerCase();
          const resultText = status === 'done' ? '已完成' : (status === 'excluded' ? '无法操作' : '未标注');
          return {
            instruction_id: instrId || null,
            instruction_system_code: String(r?.instruction_system_code || r?.system_code || '').trim(),
            status,
            result_text: resultText,
            row_qty: toQty(r),
            error_reason: String(u?.error_reason || r?.error_reason || '').trim(),
          };
        });
        const totalOrders = rows.length;
        const totalQty = rows.reduce((s: number, r: any) => s + Number(r?.row_qty || 0), 0);
        const doneRows = rows.filter((r: any) => r.status === 'done');
        const unoperatedRows = rows.filter((r: any) => r.status === 'excluded');
        const doneOrders = doneRows.length;
        const doneQty = doneRows.reduce((s: number, r: any) => s + Number(r?.row_qty || 0), 0);
        const unoperatedOrders = unoperatedRows.length;
        const unoperatedQty = unoperatedRows.reduce((s: number, r: any) => s + Number(r?.row_qty || 0), 0);
        const unmarkedOrders = Math.max(totalOrders - doneOrders - unoperatedOrders, 0);
        const unmarkedQty = Math.max(totalQty - doneQty - unoperatedQty, 0);
        const snap = {
          schema_version: 1,
          kind: 'batch_dropship_pack',
          stats: {
            total_orders: totalOrders,
            total_qty: totalQty,
            done_orders: doneOrders,
            done_qty: doneQty,
            unoperated_orders: unoperatedOrders,
            unoperated_qty: unoperatedQty,
            unmarked_orders: unmarkedOrders,
            unmarked_qty: unmarkedQty,
            operation_records_count: operationRecords.length,
            scan_match_records_count: scanMatchRecords.length,
          },
          snapshot: {
            rows,
            operation_records: operationRecords,
            scan_match_records: scanMatchRecords,
          },
        };
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `确认提交配货/装箱/贴单记录并完成阶段 - 批次：${String(batch?.system_code || `#${batchId}`)}`,
            width: 980,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color: '#595959', marginBottom: 8 }}>
                  将先固化本阶段快照（用于回看/审计），再提交并完成当前阶段。
                </div>
                <DropshipPackSnapshotBlock snapshot={snap} />
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
        if (curBatchId > 0 && updates.length > 0) {
          await request.post(`/api/v1/instruction/batches/${curBatchId}/dropship-pack-submit/`, {
            updates,
            // 仅在“提交记录并完成本阶段”时同步推进指令主状态
            sync_instruction_status: true,
          });
          try { await loadBatch(); } catch {}
        }
        if (task?.id) {
          await request.post(`/api/v1/instruction/batch-exec/tasks/${task.id}/dropship-pack/submissions/`, {
            stage_idx: stageNo,
            idempotency_key: `batch_task_${task.id}_stage_${stageNo}_dropship_pack`,
            hash: '',
            stats: snap.stats,
            snapshot: snap.snapshot,
          });
        }
      }
      // 先提交拣货（若有）
      const hasPick = stepsArr.some((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'PICK');
      if (hasPick) {
        const pickLogs = Array.isArray((window as any).__PICK_LOGS__) ? (window as any).__PICK_LOGS__ : [];
        const pickRowsForStage = (Array.isArray(stageUiSteps) ? stageUiSteps : [])
          .filter((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'PICK')
          .flatMap((s:any) => (Array.isArray(s?.data?.pick_rows) ? s.data.pick_rows : []));
        const targetSummary = buildTargetSummary(pickRowsForStage, pickLogs);
        const containerSummary = buildContainerSummary(pickLogs);
        const flags = computeFlags(targetSummary);
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: renderResultTitle(`确认提交拣货记录并完成阶段 - 批次：${String(batch?.system_code || `#${batchId}`)}`, flags),
            width: 1100,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <PickSnapshotPagedContent
                targetSummary={targetSummary}
                containerSummary={containerSummary}
                headerRecordCount={(Array.isArray(pickLogs) ? pickLogs.length : '-')}
                detailPickLogs={(Array.isArray(pickLogs) ? pickLogs : [])}
              />
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
        await submitPick(stageNo);
      }
      // 提交未完成的其他步骤
      for (const st of stepsArr) {
        if (isStepDone(st)) continue;
        const stepId = Number(st?.id);
        if (!Number.isFinite(stepId)) continue;
        const sid = String(stepId);
        await submitStep(stepId, metricsDraft[sid] || st?.collected_metrics || {});
      }
      message.success('本阶段已完成');
      const nextIdx = Math.min(currentStageIdx + 1, Math.max(stages.length - 1, 0));
      await loadTask();
      await loadStageUi();
      if (isLastStage) {
        try {
          const base = location.pathname.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/staff';
          navigate(`${base}/execution`, { replace: true });
          return;
        } catch {}
      }
      if (nextIdx !== currentStageIdx) setCurrentStageIdx(nextIdx);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  const skipCurrentStage = async () => {
    if (isReadonly) {
      message.warning('该批次已结束，当前为只读回看模式');
      return;
    }
    const cur = stages[currentStageIdx];
    if (!cur) return;
    Modal.confirm({
      title: '确认跳过本阶段？',
      content: '将把本阶段所有步骤标记为完成（跳过）。',
      okText: '跳过本阶段',
      cancelText: '取消',
      onOk: async () => {
        try {
          setLoading(true);
          const stepsArr = Array.isArray(cur?.steps) ? cur.steps : [];
          for (const st of stepsArr) {
            if (isStepDone(st)) continue;
            const stepId = Number(st?.id);
            if (!Number.isFinite(stepId)) continue;
            await submitStep(stepId, { __skipped: true });
          }
          message.success('本阶段已跳过');
          await loadTask();
          await loadStageUi();
        } catch (e:any) {
          message.error(e?.response?.data?.detail || '跳过失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const openPickSnapshot = useCallback(async () => {
    if (!task?.id || !currentStage) { message.warning('缺少执行任务上下文'); return; }
    const stageNo = Number.isFinite(Number(currentStage?.stage)) ? Number(currentStage?.stage) : null;
    if (stageNo == null) { message.warning('缺少阶段信息'); return; }
    try {
      setSnapshotLoading(true);
      const resp = await request.get(`/api/v1/instruction/batch-exec/tasks/${task.id}/picking-submissions/`, {
        params: { latest: true, t: Date.now() } as any,
      });
      const result = resp?.data?.result;
      if (!result) {
        message.info('暂无拣货快照');
        return;
      }
      const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
      if (resultStageNo != null && resultStageNo !== stageNo) {
        message.info('本阶段暂无拣货快照');
        return;
      }
      const snapRaw = (result?.snapshot || {}) as any;
      let pickLogs = Array.isArray(snapRaw?.pick_logs) ? snapRaw.pick_logs : [];
      if ((!pickLogs || pickLogs.length === 0) && Array.isArray(result?.items)) {
        pickLogs = (result.items as any[]).map((it) => ({
          container_code: String(it?.container_code || ''),
          target_code: String(it?.unit_code || ''),
          sorting_mark: String(it?.sorting_mark || ''),
          qty: Number(it?.qty || 0),
          off_shelf: !!it?.off_shelf,
        }));
      }
      const snap = {
        ...(snapRaw || {}),
        stats: Object.keys(snapRaw?.stats || {}).length ? snapRaw.stats : {
          total_qty: Number(result?.total_qty || 0),
          kinds: Number(result?.kinds || 0),
          containers: Number(result?.containers || 0),
        },
        pick_logs: pickLogs,
        submitted_at: snapRaw?.submitted_at || result?.submitted_at,
        submitted_by: snapRaw?.submitted_by || result?.submitted_by_id,
      };
      setSnapshotData(snap);
      const pickRowsForStage = (Array.isArray(stageUiSteps) ? stageUiSteps : [])
        .filter((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'PICK')
        .flatMap((s:any) => (Array.isArray(s?.data?.pick_rows) ? s.data.pick_rows : []));
      const targetSummary = buildTargetSummary(pickRowsForStage, pickLogs);
      const containerSummary = buildContainerSummary(pickLogs);
      setSnapshotSummary({
        targetSummary,
        containerSummary,
        flags: computeFlags(targetSummary),
      });
      setSnapshotVisible(true);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '加载拣货快照失败');
    } finally {
      setSnapshotLoading(false);
    }
  }, [task?.id, currentStage, stageUiSteps]);

  const openDropshipPackSnapshot = useCallback(async () => {
    if (!task?.id || !currentStage) { message.warning('缺少执行任务上下文'); return; }
    const stageNo = Number.isFinite(Number(currentStage?.stage)) ? Number(currentStage?.stage) : null;
    if (stageNo == null) { message.warning('缺少阶段信息'); return; }
    try {
      setDropshipSnapshotLoading(true);
      const resp = await request.get(`/api/v1/instruction/batch-exec/tasks/${task.id}/dropship-pack-submissions/`, {
        params: { latest: true, t: Date.now() } as any,
      });
      const result = resp?.data?.result;
      if (!result) {
        message.info('暂无配货|装箱|贴单快照');
        return;
      }
      const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
      if (resultStageNo != null && resultStageNo !== stageNo) {
        message.info('本阶段暂无配货|装箱|贴单快照');
        return;
      }
      setDropshipSnapshotData(result);
      setDropshipSnapshotVisible(true);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '加载配货|装箱|贴单快照失败');
    } finally {
      setDropshipSnapshotLoading(false);
    }
  }, [task?.id, currentStage]);

  const openWrapUpSnapshot = useCallback(async () => {
    if (!task?.id || !currentStage) { message.warning('缺少执行任务上下文'); return; }
    const stageNo = Number.isFinite(Number(currentStage?.stage)) ? Number(currentStage?.stage) : null;
    if (stageNo == null) { message.warning('缺少阶段信息'); return; }
    try {
      setWuSnapshotLoading(true);
      const resp = await request.get(`/api/v1/instruction/batch-exec/tasks/${task.id}/wrap-up-submissions/`, {
        params: { latest: true, t: Date.now() } as any,
      });
      const result = resp?.data?.result;
      if (!result) {
        message.info('暂无收尾快照');
        return;
      }
      const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
      if (resultStageNo != null && resultStageNo !== stageNo) {
        message.info('本阶段暂无收尾快照');
        return;
      }
      setWuSnapshotData(result);
      setWuSnapshotVisible(true);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '加载收尾快照失败');
    } finally {
      setWuSnapshotLoading(false);
    }
  }, [task?.id, currentStage]);

  const renderStep = (st: any) => {
    const stepId = Number(st?.id);
    const sid = String(stepId);
    const t = String(st?.step_type || st?.renderer_key || '').toUpperCase();
    const metrics = (metricsDraft[sid] || st?.collected_metrics || {});
    const onChange = (key: string, val: any) => setMetricByStep(sid, key, val);
    const stageNo = Number.isFinite(Number(currentStage?.stage)) ? Number(currentStage?.stage) : 1;

    if (t === 'PICK') {
      const stepDone = isStepDone(st);
      return (
        <PickStepRenderer
          step={st}
          data={st?.data || {}}
          metrics={metrics}
          onChange={onChange}
          onSubmit={async () => {
            await submitPick(stageNo);
            message.success('拣货已提交');
            await loadTask();
            await loadStageUi();
          }}
          instructionCodeFromParent={batch?.system_code || `批次#${batchId}`}
          canOperate={!stepDone && !isReadonly}
          taskId={task?.id}
          stageNo={stageNo}
          draftApiBase="/api/v1/instruction/batch-exec/tasks"
          initialPickLogs={(task?.meta && task.meta.pick_submission && Array.isArray(task.meta.pick_submission.pick_logs))
            ? task.meta.pick_submission.pick_logs
            : []}
        />
      );
    }
    if (t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP') {
      const stepDone = isStepDone(st);
      return (
        <SubmitWrapUpRenderer
          step={st}
          data={st?.data || {}}
          metrics={metrics}
          onChange={onChange}
          isSortCountTemplate={Array.isArray(task?.steps) && task.steps.some((s: any) => {
            const key = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
            return key === 'SORT_AND_COUNT' || key === 'SORT_COUNT';
          })}
          onSubmit={async () => {
            await submitStep(stepId, metricsDraft[sid] || {});
            message.success('收尾已提交');
            await loadTask();
            await loadStageUi();
          }}
          instructionCodeFromParent={batch?.system_code || `批次#${batchId}`}
          canOperate={!stepDone && !isReadonly}
          isBatch
          batchDetail={batch}
          taskId={task?.id}
          stageNo={stageNo}
        />
      );
    }
    if (t === 'DROPSHIP_PACK') {
      const stepDone = isStepDone(st);
      return (
        <DropshipPackRenderer
          step={st}
          data={st?.data || {}}
          metrics={metrics}
          onChange={onChange}
          instructionCodeFromParent={batch?.system_code || `批次#${batchId}`}
          canOperate={!stepDone && !isReadonly}
          taskId={task?.id}
          stageNo={stageNo}
          batchDetail={batch}
          onBatchRefresh={loadBatch}
        />
      );
    }
    if (t === 'SORT_AND_COUNT' || t === 'SORT_COUNT') {
      const stepDone = isStepDone(st);
      return (
        <SortAndCountStepRenderer
          step={st}
          data={st?.data || {}}
          metrics={metrics}
          onChange={onChange}
          onSubmit={async () => {
            await submitStep(stepId, metricsDraft[sid] || {});
            message.success('分拣清点已提交');
            await loadTask();
            await loadStageUi();
          }}
          instructionCodeFromParent={batch?.system_code || `批次#${batchId}`}
          canOperate={!stepDone && !isReadonly}
          stageNo={stageNo}
        />
      );
    }
    return (
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ color: '#8c8c8c' }}>该阶段组件尚未实现：{st?.title || st?.step_type}</div>
      </Card>
    );
  };

  return (
    <div style={{ padding: 12 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>批次执行-一件代发</span>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', fontSize: 20, fontWeight: 600, color: '#1890ff' }}
              onClick={() => {
                try {
                  const path = String((window as any)?.location?.pathname || '');
                  const base = path.startsWith('/dashboard/staff')
                    ? '/dashboard/staff'
                    : path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer';
                  navigate(`${base}/instructions/batch/${batchId}`);
                } catch {}
              }}
            >
              {batch?.system_code || `批次#${batchId}`}
            </Button>
          </div>
          <div style={{ marginLeft: 30, color: '#8c8c8c', fontSize: 14, display: 'inline-flex', gap: 16 }}>
            <span>{`指令数量${batchStats.instrCount}`}</span>
            <span>{`库存单元种类${batchStats.kind}`}</span>
            <span>{`库存单元数量${batchStats.qty}`}</span>
          </div>
        </div>
        <Spin spinning={loading}>
          {isReadonly ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="当前批次已结束（执行完成或已取消），页面已切换为只读回看模式。"
            />
          ) : null}
          {batchTips.total > 0 ? (
            <Alert
              style={{ marginBottom: 12, background: '#ffff00', border: '1px solid #ffff00', color: '#ff0000' }}
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined style={{ color: '#ff0000' }} />}
              message={(
                <span style={{ fontWeight: 700, color: '#ff0000' }}>
                  批次执行提醒（共{batchTips.total}条）
                  <span
                    onClick={() => setTipsCollapsed(v => !v)}
                    style={
                      tipsCollapsed
                        ? { cursor: 'pointer', marginLeft: 12, fontSize: 12, fontWeight: 700, color: '#ff0000', border: '1px solid #ff0000', background: 'transparent', padding: '1px 6px', borderRadius: 4 }
                        : { cursor: 'pointer', marginLeft: 12, fontSize: 12, fontWeight: 700, color: '#ffff00', border: '1px solid #ff0000', background: '#ff0000', padding: '1px 6px', borderRadius: 4 }
                    }
                  >
                    {tipsCollapsed ? '展开' : '收起'}
                  </span>
                </span>
              )}
              description={tipsCollapsed ? null : (
                <div style={{ color: '#ff0000', fontWeight: 700 }}>
                  {batchTips.groups.map((g) => {
                    const codesText = g.codes.length ? `指令：${g.codes.join(', ')}` : '';
                    return (
                      <div key={g.text} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ whiteSpace: 'pre-wrap' }}>{`${g.text}（${g.count}单）`}</span>
                        {codesText ? (
                          <Tooltip
                            placement="top"
                            title={(
                              <div style={{ maxWidth: 820, padding: '0 10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <span>指令列表</span>
                                  <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={async (e) => {
                                      e?.stopPropagation?.();
                                      try {
                                        await navigator.clipboard.writeText((g.codes || []).join('\n'));
                                        message.success('已复制');
                                      } catch {
                                        message.error('复制失败');
                                      }
                                    }}
                                  >
                                    复制
                                  </Button>
                                </div>
                                <div style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 12, boxSizing: 'border-box' }}>
                                  {(g.codes || []).map((code, idx) => (
                                    <div key={`${code}-${idx}`} style={{ padding: '2px 0' }}>{code}</div>
                                  ))}
                                </div>
                              </div>
                            )}
                          >
                            <span style={{ fontSize: 12, color: '#ff7875', cursor: 'pointer' }}>查看指令</span>
                          </Tooltip>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            />
          ) : null}
          {stages.length > 0 && (
            <div style={{ margin: '12px 0' }}>
              <Steps
                current={currentStageIdx}
                onChange={(idx) => {
                  if (idx <= currentStageIdx) { setCurrentStageIdx(idx); return; }
                  for (let i = currentStageIdx; i < idx; i += 1) {
                    const g = stages[i];
                    if (!g) continue;
                    if (!isStageCompleted(g.steps)) {
                      message.warning(`请先完成第 ${g.stage ?? (i + 1)} 阶段的必做步骤`);
                      return;
                    }
                  }
                  setCurrentStageIdx(idx);
                }}
                items={stages.map((g, i) => {
                  const nm = getStageDisplayName(g.steps || [], 2);
                  const title = `阶段${g.stage ?? i}${nm ? `-${nm}` : ''}`;
                  const isCurrent = i === currentStageIdx;
                  const titleNode = (
                    <span
                      style={
                        isCurrent
                          ? { background: '#1677ff', color: '#ffffff', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }
                          : undefined
                      }
                    >
                      {title}
                    </span>
                  );
                  return { title: titleNode };
                })}
              />
            </div>
          )}
          <div>
            {currentStage ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0 12px' }}>
                <div style={{ fontWeight: 600 }}>
                  {(() => {
                    const stepsArr = Array.isArray(currentStage?.steps) ? currentStage.steps : [];
                    const nm = getStageDisplayName(stepsArr, 2);
                    const stageHasRequired = stepsArr.some((s:any) => !!s?.required);
                    const stageHasPick = stepsArr.some((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'PICK');
                    const stageHasDropshipPack = stepsArr.some((s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'DROPSHIP_PACK');
                    const stageHasWrapUp = stepsArr.some((s:any) => {
                      const t = String(s?.step_type || s?.renderer_key || '').toUpperCase();
                      return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
                    });
                    const allDone = isStageCompleted(stepsArr);
                    const allSkipped = allDone && stepsArr.length > 0 && stepsArr.every((s:any) => !!(s?.collected_metrics && s.collected_metrics.__skipped));
                    const statusText = allDone ? (allSkipped ? '已跳过' : '已完成') : '未完成';
                    const styleBase: React.CSSProperties = { marginLeft: 12, fontWeight: 600, borderWidth: 1, borderStyle: 'solid' };
                    let statusStyle: React.CSSProperties = styleBase;
                    if (statusText === '未完成') {
                      statusStyle = { ...styleBase, background: '#fff7e6', borderColor: '#faad14', color: '#ad6800' };
                    } else if (statusText === '已完成') {
                      statusStyle = { ...styleBase, background: '#f6ffed', borderColor: '#b7eb8f', color: '#135200' };
                    } else if (statusText === '已跳过') {
                      statusStyle = { ...styleBase, background: '#f0f0f0', borderColor: '#d9d9d9', color: '#595959' };
                    }
                    return (
                      <>
                        当前操作阶段：{' '}
                        <span style={{ fontWeight: 600 }}>{`阶段${currentStage?.stage ?? (currentStageIdx + 1)}${nm ? `-${nm}` : ''}`}</span>
                        {stageHasRequired ? <Tag color="red" style={{ marginLeft: 8 }}>必做</Tag> : null}
                        <span style={{ ...statusStyle, padding: '2px 8px', borderRadius: 4 }}>{statusText}</span>
                        {(statusText === '已完成' && stageHasPick) ? (
                          <Button
                            size="small"
                            style={{ marginLeft: 8 }}
                            onClick={openPickSnapshot}
                            loading={snapshotLoading}
                          >
                            查看拣货快照
                          </Button>
                        ) : null}
                        {(statusText === '已完成' && stageHasDropshipPack) ? (
                          <Button
                            size="small"
                            style={{ marginLeft: 8 }}
                            onClick={openDropshipPackSnapshot}
                            loading={dropshipSnapshotLoading}
                          >
                            查看配货|装箱|贴单快照
                          </Button>
                        ) : null}
                        {(statusText === '已完成' && stageHasWrapUp) ? (
                          <Button
                            size="small"
                            style={{ marginLeft: 8 }}
                            onClick={openWrapUpSnapshot}
                            loading={wuSnapshotLoading}
                          >
                            查看收尾快照
                          </Button>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
                <Space>
                  {null}
                  <Tooltip title={(wrapUpSubmitGate.hasWrapUp && Number(wrapUpSubmitGate.todo || 0) > 0) ? `仍有 ${Number(wrapUpSubmitGate.todo || 0)} 个容器未放入仓位，请先完成“步骤3: 所有容器放入仓位”。` : ((wrapUpSubmitGate.hasWrapUp && wrapUpSubmitGate.blocked) ? '所有指令产出容器和仓储容器必须放入仓位,才能启用此按钮.' : undefined)}>
                    <span>
                      <Button
                        type="primary"
                        onClick={finishCurrentStage}
                        disabled={isReadonly || isStageCompleted(currentStage.steps) || (wrapUpSubmitGate.hasWrapUp && Number(wrapUpSubmitGate.todo || 0) > 0)}
                      >
                        {isReadonly ? '当前为只读回看' : '提交记录并完成本阶段'}
                      </Button>
                    </span>
                  </Tooltip>
                </Space>
              </div>
            ) : null}
            {(currentSteps || []).map((st:any, i:number) => (
              <div key={String(st?.id || i)} style={{ marginBottom: 12 }}>
                {renderStep(st)}
              </div>
            ))}
            <Modal
              title={renderResultTitle(`拣货结果 - 批次：${String(batch?.system_code || `#${batchId}`)}`, (snapshotSummary?.flags || { hasOver:false, hasUnder:false, allZero:false }))}
              open={snapshotVisible}
              onCancel={() => setSnapshotVisible(false)}
              footer={[
                <Button key="ok" type="primary" onClick={() => setSnapshotVisible(false)}>关闭</Button>,
              ]}
              width={1100}
            >
              {snapshotSummary ? (
                <PickSnapshotPagedContent
                  targetSummary={snapshotSummary.targetSummary}
                  containerSummary={snapshotSummary.containerSummary}
                  headerRecordCount={(Array.isArray(snapshotData?.pick_logs) ? snapshotData.pick_logs.length : '-')}
                  detailPickLogs={(Array.isArray(snapshotData?.pick_logs) ? snapshotData.pick_logs : [])}
                />
              ) : null}
            </Modal>
            <Modal
              title={`配货|装箱|贴单快照 - 批次：${String(batch?.system_code || `#${batchId}`)}`}
              open={dropshipSnapshotVisible}
              onCancel={() => setDropshipSnapshotVisible(false)}
              footer={[
                <Button key="ok" type="primary" onClick={() => setDropshipSnapshotVisible(false)}>关闭</Button>,
              ]}
              width={980}
            >
              {dropshipSnapshotData ? (
                <DropshipPackSnapshotBlock snapshot={dropshipSnapshotData} />
              ) : null}
            </Modal>
            <Modal
              title={`收尾快照 - 批次：${String(batch?.system_code || `#${batchId}`)}`}
              open={wuSnapshotVisible}
              onCancel={() => setWuSnapshotVisible(false)}
              footer={[
                <Button key="ok" type="primary" onClick={() => setWuSnapshotVisible(false)}>关闭</Button>,
              ]}
              width={980}
            >
              {wuSnapshotData ? (
                <WrapUpSnapshotBlock snapshot={wuSnapshotData} />
              ) : null}
            </Modal>
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default BatchExecutePage;
